import type { Product } from "schemas/product";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpcClient } from "@editor/utils/trpc";

import { z } from "zod";

// ToDo (REPL-17388): remove this old product schema and transformation when we use the correct product schema
const productOptionValueSchema = z.object({
  name: z.string(),
  value: z.string(),
});

const productVariantSchema = z.object({
  id: z.string(),
  title: z.string(),
  price: z.number(),
  availableForSale: z.boolean().optional(),
  selectedOptions: z.array(productOptionValueSchema),
});

const productOptionSchema = z.object({
  name: z.string(),
  values: z.array(z.string()),
});

const productSchema = z.object({
  id: z.string(),
  handle: z.string().optional().nullable(),
  title: z.string(),
  descriptionHtml: z.string().optional().nullable(),
  featuredImage: z.string().optional().nullable(),
  images: z.array(z.string()),
  options: z.array(productOptionSchema),
  variants: z.array(productVariantSchema),
});

const transformProductData = (
  apiProduct: Product | null,
): z.infer<typeof productSchema> | null => {
  if (!apiProduct) {
    return null;
  }

  const images: string[] = [];
  if (apiProduct.featuredMedia && apiProduct.featuredMedia.url) {
    images.push(apiProduct.featuredMedia.url);
  }
  if (apiProduct.media && Array.isArray(apiProduct.media)) {
    apiProduct.media.forEach((item) => {
      if (
        item &&
        item.type === "image" &&
        item.url &&
        !images.includes(item.url)
      ) {
        images.push(item.url);
      }
    });
  }

  const transformedOptions: z.infer<typeof productOptionSchema>[] = (
    apiProduct.options || []
  ).map((option: any) => ({
    name: option.title ?? "Unknown Option",
    values: (option.optionValues ?? [])
      .map((ov: any) => ov.value)
      .filter((v: any) => typeof v === "string"),
  }));

  const transformedVariants: z.infer<typeof productVariantSchema>[] = (
    apiProduct.variants || []
  ).map((variant: any) => {
    const selectedOptions: { name: string; value: string }[] = [];
    if (variant.optionValues && apiProduct.options) {
      variant.optionValues.forEach((variantOv: any) => {
        let parentOptionTitle = "Unknown";
        const parentOption = (apiProduct.options ?? []).find((opt: any) =>
          (opt.optionValues ?? []).some((val: any) => val.id === variantOv.id),
        );
        if (parentOption) {
          parentOptionTitle = parentOption.title;
        }

        selectedOptions.push({
          name: parentOptionTitle,
          value: variantOv.value,
        });
      });
    }

    return {
      id: variant.id,
      title: variant.title,
      price: Number.parseFloat(variant.price) || 0,
      availableForSale:
        variant.availableForSale !== undefined
          ? variant.availableForSale
          : true,
      selectedOptions: selectedOptions,
    };
  });

  return {
    id: apiProduct.id,
    handle: apiProduct.slug,
    title: apiProduct.title,
    descriptionHtml: apiProduct.description,
    featuredImage: apiProduct.featuredMedia?.url,
    images: images,
    options: transformedOptions,
    variants: transformedVariants,
  };
};

// todo (REPL-17388): move this to its own schema file

const requestProductMessageSchema = z.object({
  type: z.literal("requestProduct"),
  id: z.string(),
  payload: z.object({
    productId: z.string(),
  }),
});

const returnProductMessageSchema = z.object({
  type: z.literal("returnProduct"),
  id: z.string(),
  payload: z.object({
    product: productSchema,
  }),
});

const messageSchema = z.discriminatedUnion("type", [
  requestProductMessageSchema,
  returnProductMessageSchema,
]);

type Message = z.infer<typeof messageSchema>;

export const useIframeMessaging = () => {
  const projectId = useCurrentProjectId();

  React.useEffect(() => {
    if (!projectId) {
      return;
    }

    const handleMessageAsync = async (event: MessageEvent) => {
      if (!event.source || !event.origin) {
        return;
      }
      const messageResult = messageSchema.safeParse(event.data);
      if (!messageResult.success) {
        return;
      }
      const message = messageResult.data;

      // handlers for different message types by the parent
      let returnMessage: Message | null = null;
      if (message.type === "requestProduct") {
        const { productId } = message.payload;
        const { id: queryId } = message;

        // TODO: use the query cache
        const rawProduct = await trpcClient.product.get.query({
          type: "id",
          id: productId,
        });

        const transformedProduct = transformProductData(rawProduct);
        if (!transformedProduct) {
          return;
        }

        returnMessage = {
          type: "returnProduct",
          id: queryId,
          payload: {
            product: transformedProduct,
          },
        };
      }

      if (returnMessage) {
        event.source.postMessage(returnMessage, { targetOrigin: event.origin });
      }
    };

    const handleMessage = (event: MessageEvent) => {
      void handleMessageAsync(event);
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [projectId]);
};
